import React, { useEffect, useMemo, useState } from "react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
    ArrowUp,
    ArrowDown,
    ArrowUpDown,
    Loader2,
    Users,
    Search,
    Pencil,
    Trash2,
    Download,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { formatDDMMYYYY, todayIsoDate } from "@/lib/formatDate";
import DegreeSelect, { OTHERS_VALUE as DEGREE_OTHERS } from "@/components/DegreeSelect";
import {
    useDegrees,
    degreeLabel as fmtDegreeLabel,
} from "@/lib/degreeStore";
import {
    buildRowIndex,
    collectExportColumns,
} from "@/lib/fieldRegistry";

const ANY = "__any__";
const CONTACT_RE = /^[1-9][0-9]{9}$/;

const initialFilters = () => {
    const today = todayIsoDate();
    return {
        fromDate: today,
        toDate: today,
        search: "",
        jobRole: ANY,
        year: ANY,
        degree: ANY,
        course: ANY,
    };
};

const emptyFilters = () => ({
    fromDate: "",
    toDate: "",
    search: "",
    jobRole: ANY,
    year: ANY,
    degree: ANY,
    course: ANY,
});

function formatDate(iso) {
    return formatDDMMYYYY(iso);
}

function SortArrow({ active, dir }) {
    if (!active)
        return (
            <ArrowUpDown size={12} className="text-[#0F1F38]/35" />
        );
    return dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
}

export default function ViewApplicants() {
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const { degrees: masterDegrees } = useDegrees();

    const [draft, setDraft] = useState(initialFilters());
    const [applied, setApplied] = useState(initialFilters());
    const [sort, setSort] = useState({ key: "last_modified_date", dir: "desc" });
    const [searchWarn, setSearchWarn] = useState("");

    // Edit dialog state
    const [editOpen, setEditOpen] = useState(false);
    const [editing, setEditing] = useState(null); // applicant
    const [editForm, setEditForm] = useState(null); // form template
    const [editResponses, setEditResponses] = useState({});
    const [editError, setEditError] = useState("");
    const [editSubmitting, setEditSubmitting] = useState(false);

    // Delete confirm state
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchAll = async () => {
        try {
            const { data } = await api.get("/applicants");
            setApplicants(data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const uniqueValues = (key) => {
        const s = new Set();
        applicants.forEach((a) => {
            const v = (a[key] || "").trim();
            if (v) s.add(v);
        });
        return Array.from(s).sort();
    };

    const uniqueRoles = useMemo(() => {
        const s = new Set();
        applicants.forEach((a) => a.job_role_name && s.add(a.job_role_name));
        return Array.from(s).sort();
    }, [applicants]);
    const uniqueYears = useMemo(
        () => uniqueValues("year_of_graduation"),
        [applicants],
    );
    const uniqueDegrees = useMemo(() => uniqueValues("degree"), [applicants]);
    const uniqueCourses = useMemo(() => uniqueValues("course"), [applicants]);

    const filtered = useMemo(() => {
        let rows = applicants.slice();
        const { fromDate, toDate, search, jobRole, year, degree, course } =
            applied;

        if (fromDate) {
            const t = new Date(fromDate).getTime();
            rows = rows.filter(
                (r) => new Date(r.last_modified_date).getTime() >= t,
            );
        }
        if (toDate) {
            const t = new Date(toDate).getTime() + 24 * 60 * 60 * 1000 - 1;
            rows = rows.filter(
                (r) => new Date(r.last_modified_date).getTime() <= t,
            );
        }
        // Global search — Name / Email / Contact / College Name; min 3 chars.
        if (search.trim().length >= 3) {
            const q = search.trim().toLowerCase();
            rows = rows.filter(
                (r) =>
                    (r.name || "").toLowerCase().includes(q) ||
                    (r.email || "").toLowerCase().includes(q) ||
                    (r.contact_number || "").toLowerCase().includes(q) ||
                    (r.college_name || "").toLowerCase().includes(q),
            );
        }
        if (jobRole !== ANY) rows = rows.filter((r) => r.job_role_name === jobRole);
        if (year !== ANY)
            rows = rows.filter((r) => r.year_of_graduation === year);
        if (degree !== ANY) rows = rows.filter((r) => r.degree === degree);
        if (course !== ANY) rows = rows.filter((r) => r.course === course);

        rows.sort((a, b) => {
            const key = sort.key;
            const dir = sort.dir === "asc" ? 1 : -1;
            if (key === "last_modified_date") {
                const ta = new Date(a.last_modified_date || 0).getTime();
                const tb = new Date(b.last_modified_date || 0).getTime();
                return (ta - tb) * dir;
            }
            const va = (a[key] || "").toString().toLowerCase();
            const vb = (b[key] || "").toString().toLowerCase();
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });
        return rows;
    }, [applicants, applied, sort]);

    const toggleSort = (key) =>
        setSort((s) =>
            s.key === key
                ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
                : { key, dir: "asc" },
        );

    const applyFilters = () => {
        const q = draft.search.trim();
        if (q.length > 0 && q.length < 3) {
            setSearchWarn("Enter at least 3 characters to search.");
            return;
        }
        setSearchWarn("");
        setApplied(draft);
    };
    const resetFilters = () => {
        const e = emptyFilters();
        setDraft(e);
        setApplied(e);
        setSearchWarn("");
    };

    const openEdit = async (applicant) => {
        setEditing(applicant);
        setEditForm(null);
        setEditResponses({});
        setEditError("");
        setEditOpen(true);
        try {
            // Prefer the form linked at submission time. Fall back to the
            // most recent form for the role so admins can still edit legacy
            // records after forms have been re-published.
            let form = null;
            if (applicant.form_id) {
                try {
                    const list = (await api.get("/forms")).data;
                    form = list.find((f) => f.id === applicant.form_id);
                } catch (_) {
                    // Ignore — will fall back to fetching the latest form for the role.
                }
            }
            if (!form) {
                try {
                    const r = await api.get(
                        `/public/forms/by-role/${applicant.job_role_id}`,
                    );
                    form = r.data;
                } catch (_) {
                    // No form template at all — the modal will render with basic fields only.
                }
            }
            setEditForm(form || { fields: [] });
            // Pre-populate dynamic responses from stored record.
            const stored = applicant.responses || {};
            const seeded = {};
            const degreeLabels = masterDegrees.map(fmtDegreeLabel);
            (form?.fields || []).forEach((f) => {
                if (f.type === "select" || f.type === "degree") {
                    const v = stored[f.label] ?? "";
                    const known =
                        f.type === "degree"
                            ? degreeLabels
                            : f.options || [];
                    if (v && !known.includes(v)) {
                        seeded[f.label] = { value: "Others", other: v };
                    } else {
                        seeded[f.label] = { value: v || "", other: "" };
                    }
                } else {
                    seeded[f.label] = stored[f.label] ?? "";
                }
            });
            setEditResponses(seeded);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        }
    };

    const closeEdit = () => {
        setEditOpen(false);
        setEditing(null);
        setEditForm(null);
        setEditResponses({});
        setEditError("");
    };

    const submitEdit = async (e) => {
        e.preventDefault();
        setEditError("");
        // Flatten
        const flat = {};
        Object.entries(editResponses).forEach(([k, v]) => {
            if (v && typeof v === "object" && "value" in v) {
                flat[k] = v.value === "Others" ? v.other || "" : v.value || "";
            } else {
                flat[k] = v ?? "";
            }
        });
        // Extract standard fields for backend
        const std = {
            name: editing.name || "",
            email: editing.email || "",
            contact_number: editing.contact_number || "",
            college_name: editing.college_name || "",
            year_of_graduation: editing.year_of_graduation || "",
            degree: editing.degree || "",
            course: editing.course || "",
        };
        (editForm?.fields || []).forEach((f) => {
            const lbl = (f.label || "").toLowerCase();
            const val = flat[f.label];
            if (!val) return;
            if (f.type === "email" || lbl.includes("email")) std.email = val;
            else if (
                f.type === "phone" ||
                lbl.includes("contact") ||
                lbl.includes("phone") ||
                lbl.includes("mobile")
            )
                std.contact_number = val;
            else if (lbl === "name" || lbl.includes("full name"))
                std.name = val;
            else if (!std.name && lbl.includes("name") && !lbl.includes("college"))
                std.name = val;
            else if (lbl.includes("college")) std.college_name = val;
            else if (lbl.includes("year") && lbl.includes("graduation"))
                std.year_of_graduation = val;
            else if (lbl.includes("degree")) std.degree = val;
            else if (lbl.includes("course")) std.course = val;
        });
        if (!std.email) {
            setEditError("Email is required.");
            return;
        }
        if (!CONTACT_RE.test((std.contact_number || "").trim())) {
            setEditError(
                "Contact number must be exactly 10 digits, digits only, and cannot start with 0.",
            );
            return;
        }
        setEditSubmitting(true);
        try {
            const payload = {
                job_role_id: editing.job_role_id,
                form_id: editing.form_id || editForm?.id || null,
                ...std,
                responses: flat,
            };
            const { data } = await api.put(
                `/applicants/${editing.id}`,
                payload,
            );
            // Merge job_role_name
            data.job_role_name = editing.job_role_name;
            setApplicants((rs) =>
                rs.map((r) => (r.id === editing.id ? { ...r, ...data } : r)),
            );
            toast.success("Applicant updated.");
            closeEdit();
        } catch (err) {
            setEditError(apiErrorMessage(err));
        } finally {
            setEditSubmitting(false);
        }
    };

    const confirmDelete = (applicant) => setDeleteTarget(applicant);
    const doDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/applicants/${deleteTarget.id}`);
            setApplicants((rs) => rs.filter((r) => r.id !== deleteTarget.id));
            toast.success("Applicant deleted.");
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setDeleteTarget(null);
        }
    };

    const toggleStatus = async (applicant) => {
        const next = applicant.status === "Active" ? "Inactive" : "Active";
        try {
            const { data } = await api.patch(
                `/applicants/${applicant.id}/status`,
                { status: next },
            );
            data.job_role_name = applicant.job_role_name;
            setApplicants((rs) =>
                rs.map((r) => (r.id === applicant.id ? { ...r, ...data } : r)),
            );
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    // Client-side export — Active applicants only. Uses a canonical field
    // registry with alias resolution so different form labels ("Name" vs
    // "Full Name" vs "Candidate Name") collapse into a single "Full Name"
    // column. Custom (unmapped) fields keep their original label.
    // Applicant ID column replaced with a 1-based Sl.no.
    const exportActive = () => {
        const active = filtered.filter(
            (a) => (a.status || "Active") === "Active",
        );
        if (active.length === 0) {
            toast.info("No active applicants in the current view to export.");
            return;
        }
        const indexed = active.map((a) => ({
            row: a,
            ...buildRowIndex(a),
        }));
        const dynamicCols = collectExportColumns(indexed);

        const escape = (v) => {
            const s = v == null ? "" : String(v);
            if (/["\n,]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
            return s;
        };

        const meta = [
            { header: "Sl.no" },
            { header: "Job Role", pick: (r) => r.job_role_name || "" },
            { header: "Status", pick: (r) => r.status || "" },
            { header: "Created At", pick: (r) => r.created_date || "" },
            {
                header: "Last Updated At",
                pick: (r) => r.last_modified_date || "",
            },
        ];

        const header = [
            ...meta.map((c) => c.header),
            ...dynamicCols.map((c) => c.header),
        ]
            .map(escape)
            .join(",");

        const body = indexed
            .map(({ row, canonical, custom }, i) => {
                const metaCells = [
                    escape(i + 1),
                    ...meta.slice(1).map((c) => escape(c.pick(row))),
                ];
                const dynamicCells = dynamicCols.map((c) =>
                    escape(
                        c.kind === "canonical"
                            ? canonical.get(c.key) || ""
                            : custom.get(c.key) || "",
                    ),
                );
                return [...metaCells, ...dynamicCells].join(",");
            })
            .join("\r\n");

        const csv = "\ufeff" + header + "\r\n" + body;
        const blob = new Blob([csv], {
            type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const stamp = new Date()
            .toISOString()
            .replace(/[:.]/g, "-")
            .split("T");
        link.href = url;
        link.download = `active-applicants-${stamp[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${active.length} active applicant(s).`);
    };

    return (
        <div data-testid="admin-applicants-page">
            <AdminPageHeader
                eyebrow="Modules / Applicants"
                title="View applicants"
                description="Every candidate who has submitted an application. Edit, delete, or toggle status inline."
            />

            <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 space-y-6">
                {/* Filters */}
                <div
                    data-testid="applicants-filters"
                    className="bg-white border border-[#0F1F38]/10 p-6 space-y-5"
                >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                From Date
                            </Label>
                            <Input
                                type="date"
                                value={draft.fromDate}
                                max={draft.toDate || undefined}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        fromDate: e.target.value,
                                    }))
                                }
                                data-testid="filter-from-date"
                                className="mt-2 h-10 rounded-none border-[#0F1F38]/20"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                To Date
                            </Label>
                            <Input
                                type="date"
                                value={draft.toDate}
                                min={draft.fromDate || undefined}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        toDate: e.target.value,
                                    }))
                                }
                                data-testid="filter-to-date"
                                className="mt-2 h-10 rounded-none border-[#0F1F38]/20"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                Search (Name / Email / Contact / College)
                            </Label>
                            <div className="mt-2 relative">
                                <Search
                                    size={14}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0F1F38]/40"
                                />
                                <Input
                                    value={draft.search}
                                    onChange={(e) => {
                                        setDraft((d) => ({
                                            ...d,
                                            search: e.target.value,
                                        }));
                                        if (searchWarn) setSearchWarn("");
                                    }}
                                    placeholder="At least 3 characters"
                                    data-testid="filter-search"
                                    className="pl-9 h-10 rounded-none border-[#0F1F38]/20"
                                />
                            </div>
                            {searchWarn && (
                                <div
                                    data-testid="filter-search-warning"
                                    className="mt-1.5 text-xs text-red-700"
                                >
                                    {searchWarn}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <DropdownFilter
                            label="Job Role"
                            value={draft.jobRole}
                            onChange={(v) =>
                                setDraft((d) => ({ ...d, jobRole: v }))
                            }
                            options={uniqueRoles}
                            testid="filter-role"
                        />
                        <DropdownFilter
                            label="Year of Graduation"
                            value={draft.year}
                            onChange={(v) =>
                                setDraft((d) => ({ ...d, year: v }))
                            }
                            options={uniqueYears}
                            testid="filter-year"
                        />
                        <DropdownFilter
                            label="Degree"
                            value={draft.degree}
                            onChange={(v) =>
                                setDraft((d) => ({ ...d, degree: v }))
                            }
                            options={uniqueDegrees}
                            testid="filter-degree"
                        />
                        <DropdownFilter
                            label="Course"
                            value={draft.course}
                            onChange={(v) =>
                                setDraft((d) => ({ ...d, course: v }))
                            }
                            options={uniqueCourses}
                            testid="filter-course"
                        />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-[#0F1F38]/10">
                        <button
                            onClick={resetFilters}
                            data-testid="filter-reset"
                            className="px-5 h-10 text-xs tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors"
                        >
                            Reset
                        </button>
                        <button
                            onClick={applyFilters}
                            data-testid="filter-apply"
                            className="px-6 h-10 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors"
                        >
                            Filter
                        </button>
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="flex items-center gap-3 text-sm text-[#4B5563]">
                        <Loader2 size={16} className="animate-spin" />
                        Loading applicants…
                    </div>
                ) : applicants.length === 0 ? (
                    <div
                        data-testid="applicants-empty"
                        className="bg-white border border-dashed border-[#0F1F38]/20 py-20 px-8 text-center flex flex-col items-center"
                    >
                        <div className="w-12 h-12 flex items-center justify-center bg-[#F7F5F0] border border-[#0F1F38]/10 mb-6">
                            <Users
                                size={20}
                                strokeWidth={1.5}
                                className="text-[#0F1F38]"
                            />
                        </div>
                        <div className="font-display text-3xl leading-tight text-[#0F1F38]">
                            No applicants yet.
                        </div>
                    </div>
                ) : (
                    <div
                        data-testid="applicants-table-wrapper"
                        className="bg-white border border-[#0F1F38]/10 overflow-x-auto"
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-[#0F1F38]/10">
                            <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                Showing {filtered.length} of {applicants.length}
                            </div>
                            <button
                                type="button"
                                onClick={exportActive}
                                data-testid="applicants-export-active"
                                className="inline-flex items-center gap-2 px-4 h-9 bg-[#0F1F38] text-[#F7F5F0] text-[10px] tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors"
                                title="Export Active applicants (CSV — opens in Excel)"
                            >
                                <Download size={12} strokeWidth={2} />
                                Export Active (CSV)
                            </button>
                        </div>
                        <Table data-testid="applicants-table">
                            <TableHeader>
                                <TableRow className="bg-[#F7F5F0]">
                                    <TableHead
                                        onClick={() => toggleSort("name")}
                                        data-testid="sort-name"
                                        className="text-[10px] tracking-label uppercase text-[#0F1F38]/70 cursor-pointer select-none"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            Name
                                            <SortArrow
                                                active={sort.key === "name"}
                                                dir={sort.dir}
                                            />
                                        </span>
                                    </TableHead>
                                    <TableHead
                                        onClick={() => toggleSort("email")}
                                        data-testid="sort-email"
                                        className="text-[10px] tracking-label uppercase text-[#0F1F38]/70 cursor-pointer select-none"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            Email
                                            <SortArrow
                                                active={sort.key === "email"}
                                                dir={sort.dir}
                                            />
                                        </span>
                                    </TableHead>
                                    <TableHead
                                        onClick={() =>
                                            toggleSort("contact_number")
                                        }
                                        data-testid="sort-contact"
                                        className="text-[10px] tracking-label uppercase text-[#0F1F38]/70 cursor-pointer select-none"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            Contact Number
                                            <SortArrow
                                                active={
                                                    sort.key ===
                                                    "contact_number"
                                                }
                                                dir={sort.dir}
                                            />
                                        </span>
                                    </TableHead>
                                    <TableHead
                                        onClick={() =>
                                            toggleSort("job_role_name")
                                        }
                                        data-testid="sort-role"
                                        className="text-[10px] tracking-label uppercase text-[#0F1F38]/70 cursor-pointer select-none"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            Job Role
                                            <SortArrow
                                                active={
                                                    sort.key ===
                                                    "job_role_name"
                                                }
                                                dir={sort.dir}
                                            />
                                        </span>
                                    </TableHead>
                                    <TableHead
                                        onClick={() =>
                                            toggleSort("last_modified_date")
                                        }
                                        data-testid="sort-registration-date"
                                        className="text-[10px] tracking-label uppercase text-[#0F1F38]/70 cursor-pointer select-none"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            Registration Date
                                            <SortArrow
                                                active={
                                                    sort.key ===
                                                    "last_modified_date"
                                                }
                                                dir={sort.dir}
                                            />
                                        </span>
                                    </TableHead>
                                    <TableHead
                                        onClick={() => toggleSort("status")}
                                        data-testid="sort-status"
                                        className="text-[10px] tracking-label uppercase text-[#0F1F38]/70 cursor-pointer select-none"
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            Status
                                            <SortArrow
                                                active={sort.key === "status"}
                                                dir={sort.dir}
                                            />
                                        </span>
                                    </TableHead>
                                    <TableHead className="text-[10px] tracking-label uppercase text-[#0F1F38]/70 text-right">
                                        Actions
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={7}
                                            data-testid="applicants-no-match"
                                            className="text-center py-10 text-sm text-[#4B5563]"
                                        >
                                            No applicants match the current
                                            filters.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filtered.map((a) => {
                                        const isActive =
                                            (a.status || "Active") === "Active";
                                        return (
                                            <TableRow
                                                key={a.id}
                                                data-testid={`applicant-row-${a.id}`}
                                                className={
                                                    isActive
                                                        ? ""
                                                        : "opacity-70"
                                                }
                                            >
                                                <TableCell className="font-medium text-[#0F1F38]">
                                                    {a.name || "—"}
                                                </TableCell>
                                                <TableCell className="text-[#0F1F38]/85">
                                                    {a.email}
                                                </TableCell>
                                                <TableCell className="text-[#0F1F38]/85">
                                                    {a.contact_number}
                                                </TableCell>
                                                <TableCell className="text-[#0F1F38]/85">
                                                    {a.job_role_name}
                                                </TableCell>
                                                <TableCell className="text-[#0F1F38]/85">
                                                    {formatDate(
                                                        a.last_modified_date,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Switch
                                                            checked={isActive}
                                                            onCheckedChange={() =>
                                                                toggleStatus(a)
                                                            }
                                                            data-testid={`applicant-status-${a.id}`}
                                                        />
                                                        <span
                                                            data-testid={`applicant-status-label-${a.id}`}
                                                            className={`text-xs font-medium tracking-label uppercase ${
                                                                isActive
                                                                    ? "text-[#0F1F38]"
                                                                    : "text-[#4B5563]"
                                                            }`}
                                                        >
                                                            {isActive
                                                                ? "Active"
                                                                : "Inactive"}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="inline-flex items-center gap-1">
                                                        <button
                                                            onClick={() =>
                                                                openEdit(a)
                                                            }
                                                            data-testid={`applicant-edit-${a.id}`}
                                                            className="inline-flex items-center gap-1 px-3 h-8 border border-[#0F1F38]/20 hover:border-[#0F1F38] text-[10px] tracking-label uppercase font-semibold transition-colors"
                                                        >
                                                            <Pencil
                                                                size={11}
                                                            />
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                confirmDelete(
                                                                    a,
                                                                )
                                                            }
                                                            data-testid={`applicant-delete-${a.id}`}
                                                            className="inline-flex items-center gap-1 px-3 h-8 border border-[#0F1F38]/20 hover:border-red-700 hover:text-red-700 text-[10px] tracking-label uppercase font-semibold transition-colors"
                                                        >
                                                            <Trash2
                                                                size={11}
                                                            />
                                                            Delete
                                                        </button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            {/* Edit Dialog */}
            <Dialog
                open={editOpen}
                onOpenChange={(o) => (o ? setEditOpen(true) : closeEdit())}
            >
                <DialogContent
                    data-testid="applicant-edit-dialog"
                    className="bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                            Edit applicant · {editing?.job_role_name}
                        </div>
                        <DialogTitle className="font-display text-3xl leading-tight text-[#0F1F38]">
                            {editing?.name || editing?.email || "Applicant"}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#4B5563]">
                            All form fields are loaded from the associated
                            application template.
                        </DialogDescription>
                    </DialogHeader>

                    {editForm === null ? (
                        <div className="flex items-center gap-3 text-sm text-[#4B5563] py-8">
                            <Loader2 size={14} className="animate-spin" />
                            Loading form template…
                        </div>
                    ) : (
                        <form onSubmit={submitEdit} className="space-y-5">
                            {(editForm.fields || []).length === 0 ? (
                                <div className="text-sm text-[#4B5563] border-l-2 border-[#0F1F38]/30 pl-3">
                                    No form template is currently linked to
                                    this role. Editing basic fields only.
                                </div>
                            ) : (
                                (editForm.fields || []).map((f, i) => (
                                    <EditDynamicField
                                        key={i}
                                        field={f}
                                        idx={i}
                                        value={editResponses[f.label]}
                                        onChange={(v) =>
                                            setEditResponses((r) => ({
                                                ...r,
                                                [f.label]: v,
                                            }))
                                        }
                                    />
                                ))
                            )}

                            {editError && (
                                <div
                                    data-testid="applicant-edit-error"
                                    className="text-sm text-red-700 border-l-2 border-red-700 pl-3"
                                >
                                    {editError}
                                </div>
                            )}

                            <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#0F1F38]/10">
                                <button
                                    type="button"
                                    onClick={closeEdit}
                                    data-testid="applicant-edit-cancel"
                                    className="px-5 h-11 text-xs tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    data-testid="applicant-edit-save"
                                    disabled={editSubmitting}
                                    className="px-6 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                                >
                                    {editSubmitting && (
                                        <Loader2
                                            size={14}
                                            className="animate-spin"
                                        />
                                    )}
                                    Save
                                </button>
                            </div>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog
                open={!!deleteTarget}
                onOpenChange={(o) => !o && setDeleteTarget(null)}
            >
                <DialogContent
                    data-testid="applicant-delete-dialog"
                    className="bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none max-w-md"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl leading-tight text-[#0F1F38]">
                            Delete applicant?
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#0F1F38]/80 pt-2">
                            This will permanently remove{" "}
                            <strong>
                                {deleteTarget?.name || deleteTarget?.email}
                            </strong>
                            . This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex items-center justify-end gap-3 pt-4">
                        <button
                            onClick={() => setDeleteTarget(null)}
                            data-testid="applicant-delete-cancel"
                            className="px-5 h-11 text-xs tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={doDelete}
                            data-testid="applicant-delete-confirm"
                            className="px-6 h-11 bg-red-700 text-white text-xs tracking-label uppercase font-semibold hover:bg-red-800 transition-colors"
                        >
                            Delete
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function DropdownFilter({ label, value, onChange, options, testid }) {
    return (
        <div>
            <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                {label}
            </Label>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger
                    data-testid={testid}
                    className="mt-2 h-10 rounded-none border-[#0F1F38]/20"
                >
                    <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={ANY}>Any</SelectItem>
                    {options.map((o) => (
                        <SelectItem key={o} value={o}>
                            {o}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

function EditDynamicField({ field, idx, value, onChange }) {
    const isSelect = field.type === "select";
    const isDegree = field.type === "degree";
    const isChoice = isSelect || isDegree;
    const currentSelect = isChoice
        ? typeof value === "object" && value
            ? value
            : { value: "", other: "" }
        : null;
    const isOthers =
        isChoice && currentSelect && currentSelect.value === DEGREE_OTHERS;

    const label = (
        <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
            {field.label}
            {field.required && " *"}
        </Label>
    );

    if (field.type === "textarea") {
        return (
            <div>
                {label}
                <Textarea
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`edit-dyn-${idx}`}
                    rows={4}
                    className="mt-2 rounded-none border-[#0F1F38]/25 bg-white resize-y"
                />
            </div>
        );
    }

    if (isDegree) {
        return (
            <div>
                {label}
                <DegreeSelect
                    value={currentSelect}
                    onChange={onChange}
                    testid={`edit-dyn-${idx}`}
                    surface="white"
                />
                {isOthers && (
                    <Input
                        value={currentSelect.other || ""}
                        onChange={(e) =>
                            onChange({
                                ...currentSelect,
                                other: e.target.value,
                            })
                        }
                        placeholder="Type your degree (e.g. BE in Robotics)"
                        data-testid={`edit-dyn-other-${idx}`}
                        className="mt-2 h-11 rounded-none border-[#0F1F38]/25 bg-white"
                    />
                )}
            </div>
        );
    }

    if (isSelect) {
        const options = field.options || [];
        return (
            <div>
                {label}
                <Select
                    value={currentSelect.value}
                    onValueChange={(v) =>
                        onChange({ ...currentSelect, value: v })
                    }
                >
                    <SelectTrigger
                        data-testid={`edit-dyn-${idx}`}
                        className="mt-2 h-11 rounded-none border-[#0F1F38]/25 bg-white"
                    >
                        <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                        {options.map((o) => (
                            <SelectItem key={o} value={o}>
                                {o}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {isOthers && (
                    <Input
                        value={currentSelect.other || ""}
                        onChange={(e) =>
                            onChange({
                                ...currentSelect,
                                other: e.target.value,
                            })
                        }
                        placeholder="Please specify"
                        data-testid={`edit-dyn-other-${idx}`}
                        className="mt-2 h-11 rounded-none border-[#0F1F38]/25 bg-white"
                    />
                )}
            </div>
        );
    }

    if (field.type === "boolean") {
        const val = value === "Yes" || value === "No" ? value : "";
        return (
            <div>
                {label}
                <Select value={val} onValueChange={(v) => onChange(v)}>
                    <SelectTrigger
                        data-testid={`edit-dyn-${idx}`}
                        className="mt-2 h-11 rounded-none border-[#0F1F38]/25 bg-white"
                    >
                        <SelectValue placeholder="Select Yes or No" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                </Select>
            </div>
        );
    }

    if (field.type === "date") {
        return (
            <div>
                {label}
                <Input
                    type="date"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`edit-dyn-${idx}`}
                    className="mt-2 h-11 rounded-none border-[#0F1F38]/25 bg-white"
                />
            </div>
        );
    }

    const inputType =
        field.type === "email"
            ? "email"
            : field.type === "url"
              ? "url"
              : field.type === "phone"
                ? "tel"
                : "text";
    const isPhone = field.type === "phone";
    return (
        <div>
            {label}
            <Input
                type={inputType}
                inputMode={isPhone ? "numeric" : undefined}
                maxLength={isPhone ? 10 : undefined}
                value={value || ""}
                onChange={(e) => {
                    let v = e.target.value;
                    if (isPhone) v = v.replace(/\D+/g, "");
                    onChange(v);
                }}
                data-testid={`edit-dyn-${idx}`}
                className="mt-2 h-11 rounded-none border-[#0F1F38]/25 bg-white"
            />
        </div>
    );
}
